import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import { Row, Col, Form, Button } from "react-bootstrap";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import { jwtDecode } from "jwt-decode";
import Card from "../../../components/Card";
import url from "../../../configs/config";
import FocusError from "../../../components/error/error-focus";
import AlertNotificationSimple from "../../../components/alert/alert-notification-simple";
import InputField from "../../../components/input/input-field";
import { roleTypes } from "../../../data/roleType";
import * as SettingSelector from "../../../store/setting/selectors";
import { useSelector } from "react-redux";

const RackAdd = ({
  id,
  warehouseName,
  handleSubmitAddRack,
  setShowRackAddModal,
}) => {
  const alertRef = useRef(null);
  const navigate = useNavigate();
  const [warehouses, setWarehouses] = useState([]);
  const [warehouseId, setWarehouseId] = useState(null);
  const [errors, setErrors] = useState({});
  const [decodedToken, setDecodedToken] = useState();
  const token = localStorage.getItem("coyote-token");
  const [alert, setAlert] = useState({
    message: "",
    type: "",
    show: false,
    alertFor: "",
  });
  const [formData, setFormData] = useState({
    name: "",
    rackNumber: "",
    description: "",
    warehouseId: id ? parseInt(id) : null,
    totalRows: 1,
    totalColumns: 1,
  });
  const permissionsWarehouse = useSelector(SettingSelector?.set_permissions);
  const warehouseHasCreatePermission =
    permissionsWarehouse?.warehouse?.create === true;
  const getWarehouses = async (user) => {
    try {
      const config = {
        headers: {
          Authorization: `${token}`,
        },
      };

      if (id && warehouseName) {
        const numericId = parseInt(id);
        const warehouseOptions = [
          {
            value: numericId,
            label: warehouseName,
          },
        ];
        setWarehouses(warehouseOptions);
        setWarehouseId(numericId);
        setFormData((prev) => ({
          ...prev,
          warehouseId: numericId,
        }));
        return;
      }

      if (user) {
        const warehouseOptions = [
          {
            value: user.warehouse_id,
            label: user.warehouse,
          },
        ];
        setWarehouses(warehouseOptions);
        setWarehouseId(user.warehouse_id);
        setFormData((prev) => ({
          ...prev,
          warehouseId: user.warehouse_id,
        }));
      }
    } catch (err) {
      handleError(err, "user-edit");
    }
  };

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        const user = decoded?.user;
        setDecodedToken(user);
        const fetchData = async () => {
          await getWarehouses(user);
        };
        fetchData();
      } catch (error) {
        console.error("Failed to decode token", error);
      }
    }
  }, [token, id, warehouseName]);

  const handleSubmit = async (values, { setSubmitting }) => {
    setSubmitting(true);
    try {
      const config = {
        headers: { Authorization: `${token}` },
      };
      const response = await axios.post(
        `${url.backendURL}rack/`,
        values,
        config
      );

      if (alertRef.current) {
        alertRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }

      setAlert({
        message: response.data.message,
        type: "success",
        show: true,
        alertFor: "rack-add",
      });

      setErrors({});
      handleSubmitAddRack();
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 2000);
    } catch (err) {
      handleError(err, "rack-add");
    } finally {
      setSubmitting(false);
    }
  };

  const handleError = (err, alertFor) => {
    const { response } = err;
    if (alertRef.current) {
      alertRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
    if (
      response?.data?.error === "jwt expired" ||
      response?.data?.error === "Unauthorized. User not found"
    ) {
      localStorage.removeItem("coyote-token");

      navigate(
        `/${
          decodedToken?.roles?.some(
            (role) => role.role_type === roleTypes.SUPER_ADMIN
          )
            ? "admin"
            : "user"
        }/auth/sign-in`,
        {
          state: { status: "expired" },
        }
      );
    } else {
      const responseErrors = response?.data?.error || {};
      setErrors(responseErrors);
      setAlert({
        message: response ? response.data.message : "An error occurred",
        type: "danger",
        show: true,
        alertFor,
      });
      setTimeout(() => {
        setAlert({ show: false, alertFor: "", message: "", type: "" });
      }, 5000);
    }
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string()
      .matches(
        /^(?=.*[a-zA-Z0-9])[a-zA-Z0-9!@#$%^&*()_+=[\]{};':"\\|,.<>/?-\s]*$/,
        "Rack name must contain at least one letter or number"
      )
      .min(1, "Rack name must be at least 1 character long")
      .max(50, "Rack name must not exceed 50 characters")
      .trim()
      .required("Rack Name is required"),
    warehouseId: Yup.number()
      .integer("Warehouse must be a valid")
      .positive("Warehouse is invalid")
      .typeError("Warehouse is invalid")
      .required("Warehouse is required"),
    totalRows: Yup.number()
      .required("Rows is required")
      .integer("Rows must be an integer")
      .typeError("Rows must be a number")
      .positive("Rows must be greater than 0")
      .max(20, "Rows must be less than or equal to 20"),
    totalColumns: Yup.number()
      .required("Columns is required")
      .integer("Columns must be an integer")
      .typeError("Columns must be a number")
      .positive("Columns must be greater than 0")
      .max(20, "Columns must be less than or equal to 20"),
    rackNumber: Yup.string()
      .min(1, "Rack Number must be at least 1 character long")
      .max(20, "Rack Number must not exceed 20 characters")
      .trim()
      .required("Rack Number is required"),
    description: Yup.string()
      .trim()
      .optional()
      .min(2, "Description must be greater than 2 characters")
      .matches(/[a-zA-Z]/, "Description must contain at least one letter"),
  });

  const handleReset = (resetForm) => {
    resetForm();
    setErrors({});
  };

  const handleCancel = () => {
    if (setShowRackAddModal) {
      setShowRackAddModal(false);
    }
  };

  return (
    <>
      <Formik
        initialValues={formData}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
        enableReinitialize={true}
      >
        {({
          values,
          handleChange,
          handleBlur,
          handleSubmit,
          isSubmitting,
          resetForm,
          setFieldValue,
        }) => (
          <Form
            onSubmit={(e) => {
              e.preventDefault();
              handleSubmit();
            }}
          >
            <FocusError />
            <Row>
              <Col xl="12" lg="12" ref={alertRef}>
                <Card className="mb-0">
                  <Card.Body>
                    <AlertNotificationSimple
                      type={alert.type}
                      message={alert.message}
                      show={alert.show}
                      onClose={() => setAlert({ ...alert, show: false })}
                    />
                    <div className="new-rack-info">
                      <Row>
                        <InputField
                          className="col-md-6 form-group"
                          type="text"
                          name="name"
                          placeholder="Enter name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          asterick={true}
                          errorMessage={errors?.name}
                          label="Name"
                        />
                        <InputField
                          className="col-md-6 form-group"
                          type="text"
                          name="rackNumber"
                          placeholder="Enter rack number"
                          value={values.rackNumber}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          asterick={true}
                          errorMessage={errors?.rackNumber}
                          label="Rack Number"
                        />
                      </Row>
                      <Row>
                        <Col sm="6">
                          <Form.Group className="form-group">
                            <Form.Label
                              htmlFor="totalRows"
                              column
                              sm="4"
                              className="control-label align-self-center mb-0"
                            >
                              Rows
                              <span className="text-danger">*</span>
                            </Form.Label>

                            <Form.Control
                              type="number"
                              name="totalRows"
                              step="1"
                              value={values.totalRows}
                              min={1}
                              onWheel={(e) => e.target.blur()}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || /^\d+$/.test(value)) {
                                  setFieldValue(
                                    "totalRows",
                                    value === "" ? "" : parseInt(value)
                                  );
                                }
                              }}
                              style={{
                                fontSize: "0.8rem",
                                width: "100%",
                              }}
                            />
                            <ErrorMessage
                              name={`totalRows`}
                              component="div"
                              className="text-danger"
                            />
                            {errors?.totalRows?.map((error, index) => (
                              <span
                                key={index}
                                className="w-100 mt-1 small text-danger"
                              >
                                {error}
                                <br />
                              </span>
                            ))}
                          </Form.Group>
                        </Col>

                        <Col sm="6">
                          <Form.Group className="form-group">
                            <Form.Label
                              htmlFor="totalColumns"
                              column
                              sm="4"
                              className="control-label align-self-center mb-0"
                            >
                              Columns
                              <span className="text-danger">*</span>
                            </Form.Label>

                            <Form.Control
                              type="number"
                              name={`totalColumns`}
                              step="1"
                              value={values.totalColumns}
                              min={1}
                              onWheel={(e) => e.target.blur()}
                              onChange={(e) => {
                                const value = e.target.value;
                                if (value === "" || /^\d+$/.test(value)) {
                                  setFieldValue(
                                    "totalColumns",
                                    value === "" ? "" : parseInt(value)
                                  );
                                }
                              }}
                              style={{
                                fontSize: "0.8rem",
                                width: "100%",
                              }}
                            />

                            <ErrorMessage
                              name={`totalColumns`}
                              component="div"
                              className="text-danger"
                            />
                            {errors?.totalColumns?.map((error, index) => (
                              <span
                                key={index}
                                className="w-100 mt-1 small text-danger"
                              >
                                {error}
                                <br />
                              </span>
                            ))}
                          </Form.Group>
                        </Col>
                      </Row>
                      <Col sm="6">
                        <Form.Group className="form-group">
                          <Form.Label
                            style={{ whiteSpace: "nowrap" }}
                            className="control-label align-self-center w-100"
                          >
                            Warehouse <span className="text-danger">*</span>{" "}
                            <Select
                              closeMenuOnScroll={(e) => {
                                try {
                                  const target = e.target;

                                  if (
                                    !target ||
                                    !target.nodeType ||
                                    typeof target.closest !== "function"
                                  ) {
                                    return true;
                                  }

                                  const isMenuScroll =
                                    target.closest('[class*="MenuList"]') ||
                                    target.closest('[class*="menu"]');

                                  return !isMenuScroll;
                                } catch (error) {
                                  return true;
                                }
                              }}
                              options={warehouses}
                              value={
                                warehouses.find(
                                  (option) =>
                                    option.value === values.warehouseId
                                ) || null
                              }
                              onChange={(option) => {
                                const selectedValue = option?.value || null;
                                setWarehouseId(selectedValue);
                                setFieldValue("warehouseId", selectedValue);
                              }}
                              isDisabled={
                                warehouses.length === 1 || (id && warehouseName)
                              }
                              placeholder={
                                warehouses.length === 0 ? (
                                  <span>No warehouse found</span>
                                ) : (
                                  "Select Warehouse..."
                                )
                              }
                              noOptionsMessage={() => (
                                <div
                                  style={{
                                    textAlign: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <span style={{ color: "#666" }}>
                                    No warehouse found.
                                    {(warehouseHasCreatePermission ||
                                      decodedToken?.roles.some(
                                        (role) =>
                                          role.role_type ===
                                          roleTypes.SUPER_ADMIN
                                      )) && (
                                      <span>
                                        {" "}
                                        <Link
                                          to="/dashboard/app/warehouse-add"
                                          style={{
                                            color: "#007bff",
                                            textDecoration: "none",
                                            cursor: "pointer",
                                          }}
                                        >
                                          Add new
                                        </Link>
                                      </span>
                                    )}
                                  </span>
                                </div>
                              )}
                              className="display-content-dropdown"
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                              menuPlacement="auto"
                              isSearchable={true}
                              isClearable={false}
                              styles={{
                                option: (provided, state) => ({
                                  ...provided,
                                  backgroundColor: state.isSelected
                                    ? "#007bff"
                                    : state.isFocused
                                    ? "#e6f7ff"
                                    : "#fff",
                                  color: "#333",
                                }),
                                control: (provided) => ({
                                  ...provided,
                                  marginTop: "12px",
                                  color: "#5e667b",
                                  width: "100%",
                                  backgroundColor: "#fff",
                                }),
                                menuPortal: (provided) => ({
                                  ...provided,
                                  zIndex: 9999,
                                }),
                                menu: (provided) => ({
                                  ...provided,
                                  zIndex: 9999,
                                }),
                              }}
                            />
                          </Form.Label>
                          <ErrorMessage
                            name="warehouseId"
                            component="div"
                            className="text-danger"
                          />
                          {errors?.warehouseId?.map((error, index) => (
                            <span
                              key={index}
                              className="w-100 mt-1 small text-danger"
                            >
                              {error}
                              <br />
                            </span>
                          ))}
                        </Form.Group>
                      </Col>
                      <InputField
                        className="col-md-10 w-100 form-group"
                        as="textarea"
                        name="description"
                        placeholder="Enter description"
                        value={values.description}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        errorMessage={errors?.description}
                        label="Description"
                      />

                      <hr />
                      <div className="d-flex flex-wrap flex-md-nowrap justify-content-end gap-1 gap-md-2">
                        <Button
                          type="button"
                          className="me-2 me-md-5 px-3 px-md-4"
                          variant="btn btn-light"
                          onClick={() => handleReset(resetForm)}
                        >
                          Reset
                        </Button>

                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          variant="btn btn-primary"
                          className="me-2  me-md-5 px-3 px-md-4"
                        >
                          Submit
                        </Button>
                        <Button
                          type="button"
                          className="px-3 px-md-4"
                          variant="btn btn-danger"
                          onClick={handleCancel}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </>
  );
};

export default RackAdd;
